import React, { Fragment, useState } from 'react'
import { Action } from '../../../legacy-design-system/action'
import { PureQueryOptions } from 'apollo-client'
import { DeleteDialog } from './delete-dialog'
import { useDeleteVulnerabilitiesWithFilters } from '../api/use-delete-vulnerabilities'
import { logError } from '../../toaster'
import { FormattedMessage, useIntl } from 'react-intl'
import { IconDelete } from '@contrast-security-inc/design-system-foundations'
import { ActionProps } from '../../../legacy-design-system/action/action'
import { ActionButton } from '@contrast-security-inc/design-system-actions'

export const actionTestID = 'vuln-delete-with-filters-action'

interface Props extends Pick<ActionProps, 'size'> {
  selectedCount: number
  organizationID: string
  excludedVulnerabilities: string[]
  filters: {
    filterText: string
    quickFilter: string
    aggregations: Record<string, string[]>
  }
  additionalFilters?: { [key: string]: any }
  showDisabled?: boolean
  refetchQueries?: PureQueryOptions[]
  refresh: () => Promise<any>
  trackJob: (job: {
    id: string
    progress: number
    total: number
  }) => Promise<void>
}

export function DeleteWithFiltersAction({
  organizationID,
  filters: { aggregations, filterText, quickFilter },
  additionalFilters,
  excludedVulnerabilities,
  showDisabled,
  refresh,
  selectedCount,
  trackJob,
  size
}: Props) {
  const intl = useIntl()
  const [dialogIsOpen, setDialogIsOpen] = useState(false)
  const { mutate } = useDeleteVulnerabilitiesWithFilters({
    onSuccess: async (response) => {
      await trackJob(response.jobStatus)
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      refresh()
    },
    onError: (error) => {
      if (error.status !== 429) {
        logError(
          intl.formatMessage({
            id: 'detail_requestError'
          })
        )
        return
      }
      logError(
        intl.formatMessage({
          id: 'detail_vulnerabilities_deleteAlreadyInProgress'
        })
      )
    }
  })

  const deleteVulns = () =>
    mutate({
      organizationID,
      filters: Object.assign({}, aggregations, {
        filterText,
        quickFilter,
        ...additionalFilters
      }),
      excludedVulnerabilities
    })
  return showDisabled ? (
    <ActionButton
      isDisabled
      TooltipProps={{
        body: (
          <FormattedMessage id="title_vulnerabilities_insufficientDeletePermissions" />
        )
      }}
      IconButtonProps={{
        Icon: IconDelete,
        'aria-label': 'Delete',
        size
      }}
      data-testid={actionTestID}
    />
  ) : (
    <Fragment>
      <Action
        size={size}
        data-testid={actionTestID}
        iconTooltipMessage={{
          id: 'detail_vulnerabilities_deleteVulnerabilities'
        }}
        hideTooltip={dialogIsOpen}
        icon={IconDelete}
        onClick={() => setDialogIsOpen(true)}
      />
      <DeleteDialog
        isOpen={dialogIsOpen}
        count={selectedCount}
        onCancel={() => setDialogIsOpen(false)}
        onConfirm={() => {
          deleteVulns()
          setDialogIsOpen(false)
        }}
      />
    </Fragment>
  )
}
